<template>
  <div v-if="showModal" class="doctor-modal-overlay" @click="handleOverlayClick">
    <div class="doctor-modal" @click.stop>
      <!-- Close button (REMOVED DUPLICATE) -->
      <button class="close-button" @click="closeModal" :disabled="isLoading">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div class="doctor-header">
        <h2>{{ isOtherOpinions ? 'Other Opinions' : '' }}</h2>
        <div v-if="departmentType" class="portal-badge">
  {{ departmentType }}{{ isOtherOpinions ? '' : ' Department' }}
</div>
       
      </div>

      
      
      <div class="doctor-content">
        <p class="selection-text">
          Choose your digital twin of whom you'd like to receive the recommendation.
        </p>
        
        <div class="doctor-options">
          <!-- All Panel Consensus (always unlocked) -->
          <button 
            class="doctor-btn primary"
            :class="{ 'selected': selectedDoctor === 'ALL_COMBINED' }"
            @click="selectDoctor('ALL_COMBINED')"
            :disabled="isLoading"
          >
            <div class="doctor-info">
              <div class="doctor-name">ProxyMed Board of Experts</div>
              <div class="doctor-specialty">Comprehensive Analysis</div>
            </div>
            <div class="validated-badge">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#059669" stroke-width="2"/>
    <path d="M8 12l3 3 5-5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
  <span class="validated-text">Validated</span>
</div>
          </button>
          
          <!-- Portal-specific guidelines (locked) -->
          <button 
            v-for="guideline in portalGuidelines"
            :key="guideline.id"
            class="doctor-btn"
            :disabled="true"
            @click="selectDoctor(guideline.id)"
          >
            <div class="doctor-info">
              <div class="doctor-name">{{ guideline.name }}</div>
              <div class="doctor-specialty">{{ guideline.specialty }}</div>
            </div>
            <div class="badge-group">
              <div v-if="guideline.validated" class="validated-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#059669" stroke-width="2"/>
                  <path d="M8 12l3 3 5-5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="validated-text">Validated</span>
              </div>
              <div class="locked-badge">PRO</div>
            </div>
          </button>
        </div>
       
        
      </div>
      <div class="doctor-footer">
  <span style="background-color: #ffeb3b; padding: 4px 8px; border-radius: 4px;">
    <strong>This tool is for training purpose only!</strong>
  </span>
  <button @click="closeModal" class="btn-cancel" :disabled="isLoading">
    Cancel
  </button>
  <button 
    @click="startCoPilot" 
    class="btn-start" 
    :disabled="!selectedDoctor || isLoading"
  >
    <span v-if="!isLoading">▶ Start co-pilot</span>
    <div v-if="isLoading" class="loading-spinner"></div>
  </button>
</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

interface Guideline {
  id: string;
  name: string;
  specialty: string;
  validated?: boolean;
}

interface PortalConfig {
  displayName: string;
  guidelines: Guideline[];
}

@Component
export default class DoctorSelectionModal extends Vue {
  @Prop({ default: false }) showModal!: boolean;
  @Prop({ required: true }) branchId!: number;
  @Prop({ default: '' }) departmentType!: string; // MOVED INSIDE COMPONENT
  @Prop({ default: false }) isOtherOpinions!: boolean; // Track if opened from "See Other Opinions"
  
  isLoading: boolean = false;
  selectedDoctor: string | null = 'ALL_COMBINED';

  @Watch('showModal')
  onShowModalChange(val: boolean) {
    if (val) {
      this.selectedDoctor = 'ALL_COMBINED';
      document.body.style.overflow = 'hidden';
    }
  }
  
  // Portal configurations
  private portalConfigs: Record<string, PortalConfig> = {
    emergency: {
      displayName: 'Emergency Department',
      guidelines: [
        { id: 'AWMF_GUIDELINE', name: 'Digital Twin of AWMF Guideline', specialty: 'Emergency Medicine', validated: true },
        { id: 'EXPERT_B', name: 'Expert B ', specialty: 'Digital Twin of Emergency Specialist' },
        { id: 'EXPERT_C', name: 'Expert C', specialty: 'Digital Twin of Emergency Specialist' },
        { id: 'LOCAL_PROTOCOL', name: 'Local Protocol', specialty: 'Institutional Guidelines' }
      ]
    },
    oncology: {
      displayName: 'Oncology',
      guidelines: [
        { id: 'S3_GUIDELINE', name: 'S3 Guideline', specialty: 'Oncology Standards' },
        { id: 'EXPERT_A', name: 'Expert A Digital Twin', specialty: 'Oncology Specialist' },
        { id: 'ESMO_GUIDELINE', name: 'ESMO Guideline', specialty: 'European Oncology' },
        { id: 'LOCAL_PROTOCOL', name: 'Local Protocol', specialty: 'Institutional Guidelines' }
      ]
    },
    dentistry: {
      displayName: 'Dentistry',
      guidelines: [
        { id: 'S2K_LEITLINIE', name: 'S2k-leitlinie', specialty: 'Dental Guidelines' },
        { id: 'EXPERT_E', name: 'Expert E Digital Twin', specialty: 'Dental Specialist' },
        { id: 'LOCAL_PROTOCOL', name: 'Local Protocol', specialty: 'Institutional Guidelines' }
      ]
    },
    default: {
      displayName: 'Healthcare',
      guidelines: [
        { id: 'EXPERT_GENERAL', name: 'General Expert', specialty: 'General Medicine' },
        { id: 'LOCAL_PROTOCOL', name: 'Local Protocol', specialty: 'Institutional Guidelines' }
      ]
    }
  };
  
  get currentPortal(): string {
    // Get portal from environment variable only
    const portal = process.env.VUE_APP_PORTAL;
    
    if (portal && this.portalConfigs[portal]) {
      return portal;
    }
    
    return 'default';
  }
  
  get portalInfo(): PortalConfig {
    return this.portalConfigs[this.currentPortal] || this.portalConfigs.default;
  }
  
  get portalGuidelines(): Guideline[] {
    return this.portalInfo.guidelines;
  }
  
  mounted() {
    if (this.showModal) {
      this.selectedDoctor = 'ALL_COMBINED';
      document.body.style.overflow = 'hidden';
    }
  }
  
  handleOverlayClick() {
    // Allow closing by clicking outside
    this.closeModal();
  }
  
  selectDoctor(doctorType: string) {
    if (doctorType !== 'ALL_COMBINED') {
      // For now, other options are disabled
      return;
    }
    
    // Just mark as selected, don't trigger action yet
    this.selectedDoctor = doctorType;
  }
  
  async startCoPilot() {
    if (!this.selectedDoctor) return;
    
    this.isLoading = true;
    try {
      // Emit the selection to parent component
      this.$emit('doctor-selected', {
        doctorType: this.selectedDoctor,
        branchId: this.branchId,
        portal: this.currentPortal
      });
    } catch (error) {
      console.error('Error selecting doctor:', error);
      this.isLoading = false;
    }
  }
  
  closeModal() {
    if (this.isLoading) return;
    
    this.selectedDoctor = null;
    document.body.style.overflow = 'auto';
    this.$emit('modal-closed');
  }
  
  beforeDestroy() {
    document.body.style.overflow = 'auto';
  }
}
</script>

<style lang="scss" scoped>
.doctor-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
  box-sizing: border-box;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.doctor-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  height: auto;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}


.validated-text {
  font-size: 14px;
  color: #10b981;
  font-weight: 600;
  line-height: 1;
}
.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
}

.close-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.doctor-header {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  padding: 1rem 1.5rem;
  text-align: center;
  flex-shrink: 0;
}

.doctor-header h2 {
  margin: 0 0 0.4rem 0;
  font-size: 1.2rem;
  font-weight: 700;
}

.portal-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  display: inline-block;
  text-transform: capitalize;
}

/* ADD THIS - Department Badge Styling */
.department-badge {
  margin-top: 0.5rem;
  padding: 0.375rem 0.875rem;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.doctor-content {
  padding: 1.25rem 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.selection-text {
  font-size: 0.9rem;
  color: #1e293b;
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.5;
}

.doctor-options {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.doctor-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.doctor-btn:hover:not(:disabled) {
  border-color: #667bf2;
  background: #f8fafc;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 123, 242, 0.1);
}

.doctor-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8fafc;
}

.doctor-btn.primary {
  border-color: #2f45a5;
  background: linear-gradient(135deg, #f8fafc, #e8f0fe);
  border-width: 3px;
}

.doctor-btn.primary:hover:not(:disabled) {
  border-color: #2f45a5;
  //background: linear-gradient(135deg, #f8fafc, #e8f0fe);
  transform: translateY(-2px);
 // box-shadow: 0 4px 15px rgba(102, 123, 242, 0.15);
}

.doctor-btn.primary.selected {
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border-color: #2f45a5;
}

.doctor-info {
  text-align: left;
  flex: 1;
  min-width: 0;
}

.doctor-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doctor-specialty {
  font-size: 0.8rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doctor-btn.primary.selected .doctor-name,
.doctor-btn.primary.selected .doctor-specialty {
  color: white;
}

.selected-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.375rem 0.875rem;
  border-radius: 12px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.badge-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.validated-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #059669;
  font-weight: 500;
}



.validated-badge svg {
  width: 20px;
  height: 20px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.locked-badge {
  background: #fbbf24;
  color: #92400e;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  flex-shrink: 0;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.doctor-footer {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: stretch;
  flex-shrink: 0;
}

.doctor-footer span {
  text-align: center;
  width: 100%;
}

.btn-cancel {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: center;
}

.btn-cancel:hover:not(:disabled) {
  background: #e2e8f0;
  color: #475569;
}

.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-start {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 2px solid #059669;
  padding: 0.75rem 1.75rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
}

.btn-start:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  border-color: #047857;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.45);
}

.btn-start:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

.btn-start:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

@media (max-width: 768px) {
  .doctor-modal {
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px);
    max-height: 92vh;
    border-radius: 12px;
  }

  .doctor-header {
    padding: 0.75rem 1.25rem;
  }

  .doctor-header h2 {
    font-size: 1rem;
  }

  .portal-badge {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
  }

  .doctor-content {
    padding: 1rem 1.25rem;
  }

  .selection-text {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }

  .doctor-options {
    gap: 0.5rem;
  }

  .doctor-btn {
    padding: 0.75rem 0.875rem;
    border-radius: 10px;
  }

  .doctor-name {
    font-size: 0.875rem;
  }

  .doctor-specialty {
    font-size: 0.75rem;
  }

  .validated-badge svg {
    width: 16px;
    height: 16px;
  }

  .validated-text {
    font-size: 12px;
  }

  .locked-badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
  }

  .department-badge {
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
  }

  .doctor-footer {
    padding: 0.75rem 1.25rem;
    gap: 0.5rem;
  }

  .doctor-footer span {
    font-size: 0.8rem;
  }

  .close-button {
    top: 0.6rem;
    right: 0.6rem;
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 480px) {
  .doctor-modal {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px);
    max-height: 95vh;
    border-radius: 10px;
  }

  .doctor-header {
    padding: 0.625rem 1rem;
  }

  .doctor-header h2 {
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .doctor-content {
    padding: 0.75rem 1rem;
  }

  .selection-text {
    font-size: 0.8rem;
    margin-bottom: 0.6rem;
  }

  .doctor-btn {
    padding: 0.625rem 0.75rem;
  }

  .doctor-name {
    font-size: 0.825rem;
  }

  .doctor-specialty {
    font-size: 0.7rem;
  }

  .doctor-footer {
    padding: 0.625rem 1rem;
  }

  .btn-cancel,
  .btn-start {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    width: 100%;
  }
}
</style>